import React, { useState, useEffect, useContext, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "antd";
import Iconfont from "@components/iconfont/iconfont";
import PrivateLangContext from "@components/private_lang_context";
import TranslateContext from "@components/translate_context";
import { fetchTeamLanguages, getTeamLangCodes } from "@util/teamLang";
import { SUPPORTED_LANGUAGES, codeToLocale, syncFallbackLngInUrl } from "@util/langConfig";
import { getkey } from "@util/utils";
import { blurActiveAndWaitTranslations } from "@util/translate";
import "./index.less";

const LangTab = ({ opactiy = true, isEntrust = false, languageCodes, theme, defaultLanguage }, ref) => {
  const { componentLanguage, changeLanguage } = useContext(PrivateLangContext);
  const { translateLoading } = useContext(TranslateContext) || {};
  const { i18n } = useTranslation();
  const [lang, setLang] = useState(defaultLanguage || componentLanguage || i18n?.language || "zh");
  const [open, setOpen] = useState(false);
  // 团队语言码：缓存只用于首屏，挂载后按绑定接口刷新，确保拖动后的顺序及时生效。
  // languageCodes：显式指定语言列表时使用它，跳过团队语言（供超管后台等无团队上下文场景使用）
  const [codes, setCodes] = useState(() => languageCodes || getTeamLangCodes());
  useEffect(() => {
    if (languageCodes) return;
    const clientId = getkey("clientId");
    if (!clientId) return;
    fetchTeamLanguages(clientId).then((r) => {
      if (r?.codes?.length) setCodes(r.codes);
    });
  }, []);

  useEffect(() => {
    if (componentLanguage && lang != componentLanguage) {
      setLang(componentLanguage);
    }
  }, [componentLanguage]);

  useEffect(() => {
    if (componentLanguage || defaultLanguage || !i18n?.language) return;
    setLang(i18n.language);
  }, [componentLanguage, defaultLanguage, i18n?.language]);

  useImperativeHandle(
    ref,
    () => {
      return { changeLang };
    },
    []
  );
  const changeLang = (val) => {
    setOpen(false);
    setTimeout(async () => {
      // 切语言前先失焦当前编辑并等翻译完成：
      // 中文态未失焦就切语言时，新编辑的中文不会触发失焦翻译，目标语言槽会保留旧内容。
      await blurActiveAndWaitTranslations();
      // 有 LangProvider 时切"组件局部语言"（不动全局 i18n）；否则切全局
      if (componentLanguage) {
        changeLanguage(val);
        setLang(val);
      } else {
        // 记录"用户手动选择过语言"，避免后续进入管理页等场景被团队默认语言覆盖（与 ChangeLange.changeLang 保持一致）
        sessionStorage.setItem("lang_user_picked", "1");
        sessionStorage.setItem("lang", codeToLocale(val));
        syncFallbackLngInUrl(val);
        setLang(val);
        i18n.changeLanguage(val);
      }
    }, 600);
  };
  useEffect(() => {
    if (i18n?.language != lang && isEntrust && !componentLanguage) {
      const language = codeToLocale(i18n.language);
      sessionStorage.setItem("lang", language);
      window.lang = language;
    }
  }, [i18n?.language]);

  // 语言选项来自团队已开通语言；为空时回落中/英
  const teamOptions = codes
    .map((code) => SUPPORTED_LANGUAGES.find((language) => language.code === code))
    .filter(Boolean);
  const langOptions = teamOptions.length
    ? teamOptions
    : SUPPORTED_LANGUAGES.filter((l) => l.code === "zh" || l.code === "en");
  // 触发器按设计稿显示语言码大写（zh→ZH / en→EN / ja→JA），非 label 原文
  const triggerLabel = String(lang || "").toUpperCase();
  const isDark = theme === "dark" ? true : theme === "light" ? false : !opactiy;

  // 浮卡按设计稿自绘（稿中已注明 Web 版浮卡为自绘），不用 antd Menu 以免默认样式干扰
  const menu = (
    <div className={`LangTab-panel ${isDark ? "LangTab-panel-dark" : ""}`}>
      {langOptions.map((opt) => {
        const actived = lang === opt.code;
        return (
          <div
            key={opt.code}
            className={`LangTab-panel-item ${actived ? "LangTab-panel-item-actived" : ""} ${translateLoading ? "LangTab-panel-item-disabled" : ""}`}
            onClick={() => !translateLoading && changeLang(opt.code)}
          >
            <span className="LangTab-panel-label">
              {String(opt.code).toUpperCase()}
            </span>
            {actived && (
              <Iconfont type="icon-a-dagouxi" className="LangTab-panel-check" />
            )}
          </div>
        );
      })}
    </div>
  );
  
  // 单语言时不展示选择tab
  if (langOptions.length == 1) return <></>;

  // 团队开通语言 ≤2 种时用旧的双 tab 切换样式；>2 种时用 dropdown
  if (langOptions.length <= 2) {
    return (
      <div
        className={`LangTab LangTab-switch ${isDark ? "LangTab-dark" : ""} ${translateLoading ? "LangTab-disabled" : ""}`}
      >
        {langOptions.map((opt) => (
          <div
            key={opt.code}
            className={`LangTab-item ${lang === opt.code ? "LangTab-item-active" : ""} ${translateLoading ? "LangTab-item-disabled" : ""}`}
            onClick={() => !translateLoading && changeLang(opt.code)}
          >
            {opt.label}
          </div>
        ))}
      </div>
    );
  }

  return (
    <Dropdown
      trigger={["click"]}
      visible={open}
      onVisibleChange={(v) => !translateLoading && setOpen(v)}
      placement="bottom"
      overlay={menu}
      overlayClassName="LangTab-dropdown"
      disabled={!!translateLoading}
    >
      <div
        className={`LangTab LangTab-select ${isDark ? "LangTab-dark" : ""} ${open ? "LangTab-open" : ""} ${translateLoading ? "LangTab-disabled" : ""}`}
      >
        <div className="LangTab-trigger">
          <Iconfont type="icon-a-diqiuxi" className="LangTab-globe" />
          <span className="LangTab-current">{triggerLabel}</span>
          <Iconfont
            type="icon-a-danjiantouxiangxiaxi"
            className="LangTab-arrow-icon"
          />
        </div>
      </div>
    </Dropdown>
  );
};
export default forwardRef(LangTab);
